import React, { FC } from 'react'
import styles from './HeroCarousel.module.scss'
import LeftArrow from 'svg/SA/chevron-left-outlined-large.svg'
import RightArrow from 'svg/SA/chevron-right-outlined-large.svg'
import SALink from 'components/SALink'
import { updateCriticalImagePath } from 'lib/helpers'
import classNames from 'classnames'
import useHeroCarouselSlide from './useHeroCarouselSlide'
import useQA from 'hooks/useQA'

const IMAGE_SUBFOLDER = 'saatchi-general'

export type CarouselItem = {
  src: string
  alt: string
  title: string
  description: string
  ctaText: string
  ctaLink: string
  ctaTheme: 'light' | 'dark'
}

interface HeroCarouselProps {
  items: ReadonlyArray<CarouselItem>
}

const HeroCarousel: FC<HeroCarouselProps> = ({ items }) => {
  const { testId } = useQA()
  // Necessary to make carousel a continuous scroll
  const extendedItems = [items[items.length - 1], ...items, items[0]]

  const {
    currentIndex,
    containerDimensions,
    imageRef,
    handlePrev,
    handleNext,
    handleMouseEnter,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isTransitionEnabled,
  } = useHeroCarouselSlide({ extendedItems })

  return (
    <div
      className={styles.carouselWrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className={classNames(styles.sliderContainer, {
          [styles.sliderContainerNoTransition]: !isTransitionEnabled,
        })}
        style={{
          transform: `translateX(-${currentIndex * 100}%)`,
        }}
      >
        {extendedItems.map(
          ({ src, alt, title, description, ctaText, ctaLink, ctaTheme }, index) => {
            const isDark = ctaTheme === 'dark'

            return (
              <div {...testId('item-slide')} className={styles.slide} key={index}>
                <SALink href={ctaLink} alt={alt}>
                  <img
                    ref={index === 1 ? imageRef : null} // Just to get the responsive size of the first image to update the width and height on HTML (all images on this carousel has the same dimensions)
                    loading='lazy'
                    src={updateCriticalImagePath(IMAGE_SUBFOLDER, src)}
                    alt={alt}
                    title={title}
                    height={containerDimensions.height}
                    width={containerDimensions.width}
                  />
                </SALink>
                <div
                  className={classNames(styles.copyWrapper, {
                    [styles.copyWrapperLightTheme]: !isDark,
                    [styles.copyWrapperDarkTheme]: isDark,
                  })}
                >
                  <div
                    {...testId('copy-container')}
                    className={classNames(styles.copyContainer, {
                      [styles.copyContainerDarkTheme]: isDark,
                    })}
                  >
                    <h2>{title}</h2>
                    <h5>{description}</h5>
                    <SALink className={styles.ctaLink} href={ctaLink}>
                      <span
                        {...testId('cta-link-text')}
                        className={classNames(styles.ctaLinkSpan, {
                          [styles.ctaLinkSpanDarkTheme]: isDark,
                        })}
                      >
                        {ctaText}
                      </span>
                    </SALink>
                  </div>
                </div>
              </div>
            )
          }
        )}
      </div>

      <div className={styles.dotsWrapper}>
        {items.map((_, dotIndex) => (
          <span
            key={dotIndex}
            className={classNames(styles.dot, {
              [styles.dotActive]: dotIndex === (currentIndex - 1 + items.length) % items.length,
            })}
          />
        ))}
      </div>

      <div className={styles.carouselButtonContainer}>
        <button className={styles.carouselButton} onClick={handlePrev} aria-label='Previous slide'>
          <LeftArrow />
        </button>
        <button className={styles.carouselButton} onClick={handleNext} aria-label='Next slide'>
          <RightArrow />
        </button>
      </div>
    </div>
  )
}

export default HeroCarousel
